import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BRAND } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  "w-full rounded-xl border border-emerald-500/20 bg-white/5 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-emerald-400/70 focus:bg-white/10";

const Contact = () => {
  const { t, lang } = useLanguage();
  const emptyForm = { name: "", email: "", phone: "", service: t.contact.serviceOptions[0], message: "" };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, service: t.contact.serviceOptions[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      toast.success(t.contact.successTitle, { description: t.contact.successDesc });
      setForm({ name: "", email: "", phone: "", service: t.contact.serviceOptions[0], message: "" });
    } catch {
      toast.error(t.contact.errorTitle, { description: t.contact.errorDesc });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatos" data-testid="contact-section" className="bg-[#07120C] py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-6 sm:px-10 lg:grid-cols-2 lg:gap-20 lg:px-16">
        <Reveal>
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-emerald-400">
            {t.contact.eyebrow}
          </p>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t.contact.title}
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
            {t.contact.lead}
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex flex-col gap-4 rounded-3xl border border-emerald-500/20 bg-[#0F2117]/70 p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15">
                  <Mail className="h-5 w-5 text-emerald-400" />
                </span>
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {t.contact.emailLabel}
                  </p>
                  <p className="mt-1 break-all text-sm font-semibold text-white">
                    {BRAND.email}
                  </p>
                </div>
              </div>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-button"
                className="flex w-fit items-center gap-2 rounded-full border border-emerald-500/30 px-5 py-2.5 text-xs font-semibold text-emerald-300 transition-colors duration-300 hover:border-emerald-400/60 hover:text-emerald-100"
              >
                <Send className="h-3.5 w-3.5" />
                {t.contact.emailButton}
              </a>
            </div>

            <div className="flex flex-col gap-4 rounded-3xl border border-emerald-500/20 bg-[#0F2117]/70 p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/15">
                  <MessageCircle className="h-5 w-5 text-emerald-400" />
                </span>
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {t.contact.whatsappLabel}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-white">{BRAND.whatsappDisplay}</p>
                </div>
              </div>
              <a
                href={BRAND.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-button"
                className="flex w-fit items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-xs font-bold text-[#07120C] transition-colors duration-300 hover:bg-emerald-400"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t.contact.whatsappButton}
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="rounded-3xl border border-emerald-500/20 bg-[#0F2117]/70 p-7 backdrop-blur-md sm:p-9"
          >
            <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">
              {t.contact.formTitle}
            </h3>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="contact-name" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  {t.contact.name}
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={update("name")}
                  placeholder={t.contact.namePlaceholder}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="contact-phone" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  {t.contact.phone}
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder={t.contact.phonePlaceholder}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-email" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  {t.contact.email}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={update("email")}
                  placeholder={t.contact.emailPlaceholder}
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-service" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  {t.contact.service}
                </label>
                <select
                  id="contact-service"
                  data-testid="contact-service-select"
                  value={form.service}
                  onChange={update("service")}
                  className={`${inputClass} appearance-none bg-[#0F2117]`}
                >
                  {t.contact.serviceOptions.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#0F2117]">
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="font-mono-tech mb-2 block text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  {t.contact.message}
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  minLength={10}
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder={t.contact.messagePlaceholder}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-bold text-[#07120C] transition-colors duration-300 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {loading ? t.contact.sending : t.contact.submit}
            </button>
            <p className="mt-4 text-center text-xs text-slate-500">
              {t.contact.note} {BRAND.email}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default Contact;
